import { useState, useEffect, useMemo, memo } from 'react';
import { useProductSearch } from '../hooks/useProductSearch';
import { useProductPrices } from '../hooks/useProductPrices';
import { ProductCard } from './product/ProductCard';
import { RelatedProductsSkeleton } from './ui/Skeleton';

interface RelatedProductsProps {
  productId: string;
  currentCategoryId?: string;
  limit?: number;
  title?: string;
}

const RelatedProducts = memo(function RelatedProducts({
  productId,
  currentCategoryId,
  limit = 8,
  title = "Productos Relacionados"
}: RelatedProductsProps) {
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastFetchedProductId, setLastFetchedProductId] = useState<string | null>(null);
  const { getRelatedProducts } = useProductSearch();

  // Obtener IDs de productos relacionados
  const productIds = useMemo(() => {
    return relatedProducts.map(p => p.id);
  }, [relatedProducts]);

  // Usar el hook centralizado de precios
  const { prices, loading: pricesLoading } = useProductPrices(productIds, {
    enabled: productIds.length > 0
  });

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      // Evitar llamadas duplicadas para el mismo producto
      if (lastFetchedProductId === productId) {
        return;
      }

      try {
        setLoading(true);
        const products = await getRelatedProducts(productId, limit);
        setRelatedProducts(products);
        setLastFetchedProductId(productId);
      } catch (error) {
        console.error('Error fetching related products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [productId, limit]);

  if (loading || pricesLoading) {
    return <RelatedProductsSkeleton count={limit} title={title} />;
  }

  if (relatedProducts.length === 0) {
    return null;
  }

  return (
    <div className="py-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{title}</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {relatedProducts.map((product) => {
          const priceData = prices[product.id];

          return (
            <ProductCard
              key={product.id}
              product={{
                id: product.id,
                name: product.name,
                main_image: product.main_image,
                code: product.code
              }}
              priceInfo={priceData ? {
                price: priceData.isOnSale && priceData.offerPrice ? priceData.offerPrice : priceData.price,
                originalPrice: priceData.isOnSale ? priceData.price : null,
                isOnSale: priceData.isOnSale
              } : {
                price: product.base_price || 0
              }}
              variant="minimal"
              showCode={true}
              showAddToCart={false}
            />
          );
        })}
      </div>
    </div>
  );
});

export default RelatedProducts;
